import { useEffect, useState } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { formatCurrency } from '../../lib/utils';
import { DollarSign, Coins, TrendingUp, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
    paid: { label: 'Paid', icon: CheckCircle, color: 'text-emerald-400 bg-emerald-500/10' },
    pending: { label: 'Pending', icon: Clock, color: 'text-amber-400 bg-amber-500/10' },
    expired: { label: 'Expired', icon: XCircle, color: 'text-red-400 bg-red-500/10' },
    failed: { label: 'Failed', icon: AlertCircle, color: 'text-red-400 bg-red-500/10' },
};

function StatCard({ icon: Icon, label, value, sub, color = 'text-gold' }) {
    return (
        <Card className="bg-[#121620] border-white/10">
            <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                    <p className="text-sm text-muted-foreground">{label}</p>
                    <div className="w-9 h-9 rounded-lg bg-gold/10 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-gold" />
                    </div>
                </div>
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
            </CardContent>
        </Card>
    );
}

export default function AdminDeposits() {
    const [transactions, setTransactions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        Promise.all([
            axios.get(`${API}/api/admin/transactions`, { withCredentials: true }).then(r => setTransactions(r.data)),
            axios.get(`${API}/api/admin/transactions/stats`, { withCredentials: true }).then(r => setStats(r.data)),
        ]).catch(console.error).finally(() => setLoading(false));
    }, []);

    const filtered = filter === 'all' ? transactions : transactions.filter(t => t.payment_status === filter);

    if (loading) {
        return (
            <DashboardLayout type="admin">
                <div className="flex items-center justify-center h-64">
                    <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout type="admin">
            <div className="space-y-8" data-testid="admin-deposits">
                <div>
                    <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white">Deposits & Transactions</h1>
                    <p className="text-muted-foreground mt-1">Track all payment transactions across operators</p>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard icon={DollarSign} label="Total Revenue" value={formatCurrency(stats.total_revenue)} color="text-emerald-400" />
                        <StatCard icon={Coins} label="Credits Sold" value={stats.total_credits_sold.toLocaleString()} />
                        <StatCard icon={TrendingUp} label="Completed" value={stats.total_transactions} />
                        <StatCard icon={Clock} label="Pending" value={stats.pending_transactions} color="text-amber-400" />
                    </div>
                )}

                {/* Filter Tabs */}
                <div className="flex items-center gap-2 flex-wrap">
                    {['all', 'paid', 'pending', 'expired'].map(f => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${
                                filter === f
                                    ? 'bg-gold/15 text-gold border border-gold/30'
                                    : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10'
                            }`}
                            data-testid={`filter-${f}`}
                        >
                            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                            {f === 'all' && ` (${transactions.length})`}
                        </button>
                    ))}
                </div>

                {/* Table */}
                <Card className="bg-[#121620] border-white/10 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="transactions-table">
                            <thead>
                                <tr className="border-b border-white/10 text-left">
                                    <th className="px-4 py-3 text-xs text-muted-foreground font-medium">Date</th>
                                    <th className="px-4 py-3 text-xs text-muted-foreground font-medium">Operator</th>
                                    <th className="px-4 py-3 text-xs text-muted-foreground font-medium">Type</th>
                                    <th className="px-4 py-3 text-xs text-muted-foreground font-medium">Credits</th>
                                    <th className="px-4 py-3 text-xs text-muted-foreground font-medium">Amount</th>
                                    <th className="px-4 py-3 text-xs text-muted-foreground font-medium">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground text-sm">
                                            No transactions found
                                        </td>
                                    </tr>
                                )}
                                {filtered.map(t => {
                                    const sc = STATUS_CONFIG[t.payment_status] || STATUS_CONFIG.pending;
                                    const StatusIcon = sc.icon;
                                    const date = t.created_at ? new Date(t.created_at) : null;
                                    return (
                                        <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`tx-row-${t.id}`}>
                                            <td className="px-4 py-3 text-sm text-white/70">
                                                {date ? (
                                                    <>
                                                        <span className="block">{date.toLocaleDateString()}</span>
                                                        <span className="text-[10px] text-muted-foreground">{date.toLocaleTimeString()}</span>
                                                    </>
                                                ) : 'N/A'}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-white">{t.operator_name || 'N/A'}</td>
                                            <td className="px-4 py-3">
                                                <span className="text-xs px-2 py-1 rounded bg-white/5 text-white/70 capitalize">{t.type}</span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gold font-medium">{t.credits || '-'}</td>
                                            <td className="px-4 py-3 text-sm text-white font-medium">{formatCurrency(t.amount)}</td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${sc.color}`}>
                                                    <StatusIcon className="w-3 h-3" />
                                                    {sc.label}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            </div>
        </DashboardLayout>
    );
}
